import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { authBackend } from "../lib/authBackend";
import { signup } from "../lib/api";

const inputCls =
  "w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500";

export default function Signup() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail || !password) {
      toast.error("Enter your email and a password.");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await signup(cleanEmail, password);
      const { error } = await authBackend.signInWithPassword({ email: cleanEmail, password });
      if (error) throw error;
      toast.success("Account created.");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Signup failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-sm rounded-xl border border-slate-200 bg-white p-6 sm:p-8 space-y-5">
        <div className="text-center">
          <h1 className="font-work text-xl font-semibold text-slate-900">Set up your password</h1>
          <p className="text-sm text-slate-500 mt-1">
            Use the email the dealership admin added you with.
          </p>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
          <div>
            <label className="uplabel mb-1.5 block">Email</label>
            <input
              data-testid="signup-email-input"
              type="email"
              autoFocus
              className={inputCls}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <label className="uplabel mb-1.5 block">New Password</label>
            <input
              data-testid="signup-password-input"
              type="password"
              className={inputCls}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <div>
            <label className="uplabel mb-1.5 block">Confirm Password</label>
            <input
              data-testid="signup-confirm-password-input"
              type="password"
              className={inputCls}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </div>
          <button
            type="submit"
            data-testid="signup-submit-button"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2.5 text-sm transition-colors disabled:opacity-60"
          >
            {loading ? <><Loader2 className="animate-spin" size={16} /> Creating…</> : "Create account"}
          </button>
        </form>
        <p className="text-center text-sm text-slate-500">
          Already set your password?{" "}
          <Link to="/login" className="text-blue-600 hover:underline font-medium">
            Log in
          </Link>
        </p>
      </div>
    </div>
  );
}
